import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/axios';

export interface SpinWheelItem {
  id: number;
  type: string;
  name: string;
  amount: number;
  image: string;
  probability: number;
  color: string;
  CodeName128: string;
  colorClass: string;
}

export interface SpinUserSpins {
  daily_spins: number;
  available_spins: number;
  spins_used: number;
  total_spins_used: number;
  last_reset: string;
}

export interface SpinRecentRewardRow {
  reward_name: string;
  reward_image: string;
  reward_quantity: number;
  rewarded_at: string;
}

export interface SpinRecentRewardFormatted {
  name: string;
  image: string;
  quantity: number;
  time_ago: string;
}

export interface SpinState {
  spinData: SpinWheelItem[];
  userSpins: SpinUserSpins;
  recentRewards: SpinRecentRewardRow[];
}

export const SPIN_QUERY_KEY = ['spin', 'state'] as const;

export async function fetchSpinState(): Promise<SpinState> {
  const { data } = await api.get<SpinState & { status: string }>('/api/v1/spin');
  return {
    spinData: data.spinData ?? [],
    userSpins: data.userSpins,
    recentRewards: data.recentRewards ?? [],
  };
}

export async function fetchRecentSpinRewards(): Promise<SpinRecentRewardFormatted[]> {
  const { data } = await api.get<{ success: boolean; rewards: SpinRecentRewardFormatted[] }>(
    '/api/v1/spin/recent-rewards',
  );
  return data.rewards ?? [];
}

export function useSpinState() {
  return useQuery({
    queryKey: SPIN_QUERY_KEY,
    queryFn: fetchSpinState,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
}

export function useSpinMutations() {
  const queryClient = useQueryClient();

  const invalidate = () => queryClient.invalidateQueries({ queryKey: SPIN_QUERY_KEY });

  const spin = useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{
        success: boolean;
        message?: string;
        item?: SpinWheelItem;
        winningIndex?: number;
        remainingSpins?: number;
        spinId?: number;
      }>('/api/v1/spin/spin', {});
      return data;
    },
  });

  const deliverReward = useMutation({
    mutationFn: async (itemId: number) => {
      const { data } = await api.post<{ success: boolean; message: string }>(
        '/api/v1/spin/deliver-reward',
        { item_id: itemId },
      );
      return data;
    },
    onSuccess: (data) => {
      if (data.success) {
        window.setTimeout(() => invalidate(), 300);
      }
    },
  });

  const resetSpins = useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{ success: boolean; message?: string }>(
        '/api/v1/spin/reset-spins',
        {},
      );
      return data;
    },
    onSuccess: (data) => {
      if (data.success) invalidate();
    },
  });

  return { spin, deliverReward, resetSpins, invalidate, fetchRecentSpinRewards };
}

export function prefetchSpinState(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.prefetchQuery({
    queryKey: SPIN_QUERY_KEY,
    queryFn: fetchSpinState,
    staleTime: 30_000,
  });
}
